import { useEffect, useState } from 'react';
import { CssVarsProvider, extendTheme, useColorScheme } from '@mui/joy/styles';
import GlobalStyles from '@mui/joy/GlobalStyles';
import CssBaseline from '@mui/joy/CssBaseline';
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
// import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import logo from '../../assets/images/logo.svg';
import bgDay from '../../assets/images/bg_day.png';
import bgNight from '../../assets/images/bg_day.png';
// import bgDay from '../../assets/images/bg_day.jpeg';
// import bgNight from '../../assets/images/bg_night.jpeg';
import { Link, Outlet, useNavigate, useParams, useLocation } from 'react-router';
import useUser from './useUser';
import LargeLoader from '../LargeLoader';
import { gql, useQuery } from '@apollo/client';
import { parseGraphQLError } from '../../utils';
import JoyLink from '@mui/joy/Link';
import { useIntercom } from '../useIntercom';
import { useDocumentTitleForAuth } from '../useDocumentTitle';

const CLIENT_NAVIGATION_WORKFLOW = [
  { path: "sign-up", label: "Sign Up", condition: user => !user, routesToSkip: ["sign-in", "intro", "reset-password", "sign-up"] },
  { path: "confirm-email", label: "Confirm Email", condition: user => user && !user.emailConfirmed, isBreaking: true },
  { path: "start-company", label: "Start Company", condition: user => user && user.emailConfirmed && !user.talent && !user.client },
  { path: "onboarding/ats-decision", label: "ATS Decision", condition: user => user && user.client && user.client.usesATS === null, routesToSkip: ["onboarding/"] },
  { path: "onboarding/ats-selector", label: "ATS Selector", condition: user => user && user.client && user.client.usesATS === true && !user.client.selectedATSProvider, routesToSkip: ["onboarding/"] },
  { path: "jobs", label: "Jobs", condition: user => user && user.client && user.client.usesATS !== null && (user.client.usesATS === false || !!user.client.selectedATSProvider), routesToSkip: ["onboarding/"] },
  { path: "applications", label: "Account", condition: user => user && user.talent && !user.client },
];

const TALENT_NAVIGATION_WORKFLOW = [
  { path: "intro", label: "Introduction", condition: () => true, routesToSkip: ["sign-in", "sign-up", "reset-password", "intro"] },
  { path: "sign-up", label: "Sign Up", condition: user => !user, routesToSkip: ["sign-in", "intro", "reset-password"] },
  { path: "confirm-email", label: "Confirm Email", condition: user => user && !user.emailConfirmed, isBreaking: true },
  { path: "apply-profile", label: "Apply Profile", condition: user => user && user.emailConfirmed, routesToSkip: ["intro"] },
  { path: "questionnaire", label: "Questionnaire", condition: (user, clientId, jobId, jobInfo) => {
    const application = user?.talent?.applications?.find(app => app.job._id === jobId && app.client._id === clientId);
    return !!application && hasScreeningQuestions(application.job.questions ?? jobInfo?.questions);
  } },
  { path: "video-interview-intro", label: "Video Interview", condition: (user, clientId, jobId, jobInfo) => {
    const application = user?.talent?.applications?.find(app => app.job._id === jobId && app.client._id === clientId);
    if (!application) return false;
    const hasQuestions = hasScreeningQuestions(application.job.questions ?? jobInfo?.questions);
    return !hasQuestions || application.questionnaire?.passed;
  } },
  { path: "/applications", label: "Applications", condition: (user, clientId, jobId) => user && user.talent && user.talent.applications?.some(app => app.interview?.isFinished && app.job._id === jobId && app.client._id === clientId) },
];

const hasScreeningQuestions = (questions) => {
  if (!questions) return false;
  try {
    return JSON.parse(questions).length > 0;
  } catch {
    return false;
  }
};

const getNavigationPath = (user, clientId, jobId, isApply, jobInfo) => {
  const workflow = isApply ? TALENT_NAVIGATION_WORKFLOW : CLIENT_NAVIGATION_WORKFLOW;
  let path;
  for (const step of workflow) {
    if (step.condition(user, clientId, jobId, jobInfo) && !step.routesToSkip?.some(route => window.location.pathname.includes(route))) {
      path = step.path;
      if (step.isBreaking)
        break;
    }
  }
  console.log("[AUTH LAYOUT] Navigation path determined:", path);
  return path;
};

export const GET_JOB_INFO = gql`
  query GetJobInfo($clientId: String!, $jobId: String!) {
    getJobInfo(clientId: $clientId, jobId: $jobId) {
      _id
      title
      hideClientName
      questions
      client {
        _id
        company
      }
    }
  }
`;

export function ColorSchemeToggle(props) {
  const { onClick, ...other } = props;
  const { mode, setMode } = useColorScheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  return (
    <IconButton
      aria-label="toggle light/dark mode"
      size="sm"
      variant="outlined"
      disabled={!mounted}
      onClick={(event) => {
        setMode(mode === 'light' ? 'dark' : 'light');
        onClick?.(event);
      }}
      sx={{ maxHeight: 32 }}
      {...other}
    >
      {mode === 'light' ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}
    </IconButton>
  );
}

const customTheme = extendTheme({ defaultColorScheme: 'dark' });

export default function AuthLayout({ children, isApply }) {
  const { user, loading: loadingUser, error: userError } = useUser();
  useIntercom(user);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isSelectPlan = pathname.includes("select-plan");
  const { clientId, jobId } = useParams();
  const { data: jobInfo, loading: loadingJob, error } = useQuery(GET_JOB_INFO, {
    variables: { clientId, jobId },
    skip: !clientId || !jobId,
  });
  const loading = loadingUser || loadingJob;

  // Set document title for auth pages
  useDocumentTitleForAuth({ jobInfo, isApply, skip: loading });

  let errorText = error && parseGraphQLError(error);
  const hasUserError = userError && parseGraphQLError(userError) !== "Unauthenticated";

  useEffect(() => {
    if (loadingUser) 
      return;
    console.log("[AUTH LAYOUT] user update", user);
    // if (user) {
    //   console.log("[AUTH LAYOUT] got user", user);
    //   if (user.emailConfirmed) {
    //     if (!isApply) {
    //       if (user.client)
    //         navigate("/jobs");
    //       else if (user.talent)
    //         navigate("/profile");
    //       else
    //         navigate("/start-company");
    //     } else {
    //       const application = user.talent?.applications?.find(app => app.job._id === jobId && app.client._id === clientId);
    //       if (application) {
    //         if (application.questionnaire) {
    //           if (application.questionnaire.passed) {
    //             navigate(`/company/${clientId}/job/${jobId}/video-interview`);
    //           } else {
    //             navigate(`/applications`);
    //           }
    //         } else {
    //           navigate("questionnaire");
    //         }
    //       } else {
    //         navigate("apply-profile");
    //       }
    //     }
    //   } else {
    //     navigate("confirm-email");
    //   }
    // } else {
    //   const routesToSkip = ["sign-in", "sign-up", "intro", "reset-password"];
    //   if (!routesToSkip.some(route => window.location.pathname.includes(route)))
    //     navigate("sign-in");
    // }
    // if (ROUTES_TO_SKIP.some(route => window.location.pathname.includes(route))) {
    //   console.log("[AUTH LAYOUT] Skipping navigation for route", window.location.pathname);
    //   return;
    // }
    const path = getNavigationPath(user, clientId, jobId, isApply, jobInfo?.getJobInfo);
    if (path)
      navigate(path);
  }, [user, navigate, isApply, clientId, jobId, loadingUser, jobInfo]);

  const isDemo = user?.client?._id === clientId;

  return (
    <CssVarsProvider theme={customTheme} disableTransitionOnChange>
      <CssBaseline />
      <GlobalStyles
        styles={{
          ':root': {
            '--Form-maxWidth': '800px',
            '--Transition-duration': '0.4s', // set to `none` to disable transition
          },
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 1,
              transform: 'scale(1)',
            },
            '50%': {
              opacity: 0.7,
              transform: 'scale(1.1)',
            },
          },
          '@keyframes demoGlow': {
            '0%, 100%': {
              boxShadow: '0 4px 12px rgba(245,158,11,0.3)',
            },
            '50%': {
              boxShadow: '0 6px 20px rgba(245,158,11,0.5), 0 0 30px rgba(245,158,11,0.2)',
            },
          },
        }}
      />
      <Box
        sx={(theme) => ({
          width: { xs: '100%', md: isSelectPlan ? '100%' : '50vw' },
          transition: 'width var(--Transition-duration)',
          transitionDelay: 'calc(var(--Transition-duration) + 0.1s)',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          backdropFilter: theme.palette.mode === 'light' ? 'brightness(5) blur(60px)' : 'blur(15px)',
          backgroundColor: 'rgba(255 255 255 / 0.2)',
          [theme.getColorSchemeSelector('dark')]: {
            backgroundColor: 'rgba(19 19 24 / 0.4)',
          },
        })}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100dvh',
            width: '100%',
            px: 2,
          }}
        >
          <Box
            component="header"
            sx={{ py: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            {jobInfo?.getJobInfo ?
              <Box
                sx={{
                  position: 'relative',
                  background: (theme) => theme.palette.mode === 'light'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(15,23,42,0.8) 100%)',
                  borderRadius: '20px',
                  padding: '24px 28px',
                  backdropFilter: 'blur(20px)',
                  border: (theme) => theme.palette.mode === 'light'
                    ? '1px solid rgba(255,255,255,0.6)'
                    : '1px solid rgba(148,163,184,0.2)',
                  boxShadow: (theme) => theme.palette.mode === 'light'
                    ? '0 20px 40px rgba(0,0,0,0.1), 0 4px 8px rgba(0,0,0,0.05)'
                    : '0 20px 40px rgba(0,0,0,0.4), 0 4px 8px rgba(0,0,0,0.2)',
                  maxWidth: { xs: '400px', sm: '600px' },
                  minWidth: { xs: '250px', sm: '300px' },
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                    borderRadius: '20px 20px 0 0',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 8px 16px rgba(102,126,234,0.3)',
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '18px',
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      {jobInfo.getJobInfo.client.company.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      level="h3"
                      sx={{
                        fontSize: '22px',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        mb: 0.5,
                        background: (theme) => theme.palette.mode === 'light'
                          ? 'linear-gradient(135deg, #1e293b 0%, #475569 100%)'
                          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: 'none',
                      }}
                    >
                      {jobInfo.getJobInfo.title}
                    </Typography>
                    {!jobInfo.getJobInfo.hideClientName &&
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          level="body-sm"
                          sx={{
                            color: (theme) => theme.palette.mode === 'light' ? '#64748b' : '#94a3b8',
                            fontWeight: 500,
                            fontSize: '14px',
                          }}
                        >
                          at
                        </Typography>
                        <Typography
                          level="title-md"
                          sx={{
                            color: (theme) => theme.palette.mode === 'light' ? '#475569' : '#cbd5e1',
                            fontWeight: 600,
                            fontSize: '16px',
                          }}
                        >
                          {jobInfo.getJobInfo.client.company}
                        </Typography>
                      </Box>
                    }
                  </Box>
                </Box>
                {/* Status indicator - show demo badge if demo mode, otherwise show status dot */}
                {isDemo ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.5,
                      py: 0.25,
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      boxShadow: '0 4px 12px rgba(245,158,11,0.3)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(8px)',
                      animation: 'demoGlow 3s ease-in-out infinite',
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        background: '#fbbf24',
                        boxShadow: '0 0 4px rgba(251,191,36,0.6)',
                        animation: 'pulse 2s infinite',
                      }}
                    />
                    <Typography
                      level="body-xs"
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}
                    >
                      Demo
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      boxShadow: '0 0 8px rgba(16,185,129,0.4)',
                      animation: 'pulse 2s infinite',
                    }}
                  />
                )}
              </Box> :
              <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                <Link to="/">
                  <img src={logo} alt="Cavalry" />
                </Link>
              </Box>
            }
            <ColorSchemeToggle />
          </Box>
          {hasUserError ?
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography level="h4" sx={{ mb: 2 }}>
                Error loading user information
              </Typography>
              {hasUserError &&
                <Typography level="body-md" sx={{ mb: 2 }}>
                  {parseGraphQLError(userError)}
                </Typography>
              }
              <JoyLink level="title-sm" component={Link} to="/sign-in" onClick={() => window.location.reload()}>
                Try signing in again
              </JoyLink>
            </Box> : (
              errorText ?
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography level="h4" sx={{ mb: 2 }}>
                    Error loading job information
                  </Typography>
                  <Typography level="body-md" sx={{ mb: 2 }}>
                    {errorText}
                  </Typography>
                </Box> :
                loading ? <LargeLoader /> :
                  <Box sx={{
                    mt: { xs: 0, md: isSelectPlan ? -4 : 0 },
                  }}>
                    {children}
                    <Outlet />
                  </Box>
            )}
          <Box component="footer" sx={{ py: 3 }}>
            <Typography level="body-xs" sx={{ textAlign: 'center', position: 'absolute', bottom: 10, left: 0, right: 0 }}>
              © Cavalry {new Date().getFullYear()}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={(theme) => ({
          height: '100%',
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          left: { xs: 0, md: '50vw' },
          transition:
            'background-image var(--Transition-duration), left var(--Transition-duration) !important',
          transitionDelay: 'calc(var(--Transition-duration) + 0.1s)',
          backgroundColor: 'background.level1',
          backgroundSize: 'cover',
          backgroundPosition: 'bottom',
          backgroundRepeat: 'no-repeat',
          backgroundImage:
            `url(${bgDay})`,
          [theme.getColorSchemeSelector('dark')]: {
            backgroundImage:
              `url(${bgNight})`,
          },
        })}
      />
    </CssVarsProvider>
  );
}
